import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MegaphoneSimple, PushPin, Plus } from "@phosphor-icons/react";

const KINDS = [
  { v: "general", label: "General", color: "bg-zinc-50 border-zinc-200 text-zinc-800" },
  { v: "policy", label: "Policy", color: "bg-blue-50 border-blue-200 text-blue-800" },
  { v: "celebration", label: "Celebration", color: "bg-adb-green/10 border-adb-green/40 text-adb-navy" },
  { v: "urgent", label: "Urgent", color: "bg-red-50 border-red-200 text-red-700" },
];

export default function Announcements() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
  const isHR = ["super_admin", "company_admin", "hr"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", kind: "general", pinned: false });

  const load = () => api.get("/announcements", { params }).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const post = async () => {
    try {
      const payload = { ...form };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/announcements", payload);
      toast.success("Announcement posted");
      setOpen(false);
      setForm({ title: "", body: "", kind: "general", pinned: false });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Engagement · Company feed</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Announcements</h1>
          <p className="text-sm text-zinc-500 mt-2">Broadcast company news, policy updates and celebrations to the whole team.</p>
        </div>
        {isHR && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="ann-post-btn"><Plus size={16} className="mr-1.5" /> Post announcement</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">New announcement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="ann-title-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Body *</Label><Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-sm" data-testid="ann-body-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Kind</Label>
                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="ann-kind-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-end gap-2 pb-2 cursor-pointer">
                    <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} data-testid="ann-pin-check" />
                    <span className="text-xs">Pin to top</span>
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={post} disabled={!form.title.trim() || !form.body.trim()} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="ann-send-btn">Publish</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3" data-testid="ann-list">
        {rows.length === 0 && <div className="border border-zinc-200 bg-white p-12 rounded-sm text-center"><MegaphoneSimple size={32} className="mx-auto text-zinc-300" /><div className="text-sm text-zinc-500 mt-2">No announcements yet.</div></div>}
        {rows.map((r) => {
          const kind = KINDS.find((k) => k.v === r.kind) || KINDS[0];
          return (
            <div key={r.id} className={`border rounded-sm p-5 ${kind.color}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="rounded-sm bg-white/70 border-current text-[10px] uppercase">{kind.label}</Badge>
                    {r.pinned && <div className="flex items-center gap-0.5 text-[10px] uppercase tracking-widest font-bold"><PushPin size={10} weight="fill" /> Pinned</div>}
                  </div>
                  <div className="font-display text-xl font-bold mt-2">{r.title}</div>
                  <div className="text-sm mt-1.5 whitespace-pre-wrap leading-relaxed">{r.body}</div>
                  <div className="text-[10px] mt-3 opacity-70 font-mono-plex">by {r.author_name} · {new Date(r.created_at).toLocaleString()}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
